/**
 * F3 (paridad Ledgeria) — Tesorería: cuentas bancarias, extractos y
 * conciliación.
 *
 * Importes SIEMPRE en céntimos con signo desde el punto de vista del titular:
 * positivo entra dinero, negativo sale. Es la convención contraria a la del
 * fichero del banco (que habla de su propio debe/haber), y se traduce en el
 * parser para que aquí no haya que pensarlo.
 */
import { z } from 'zod';
export const BankTransactionStatusSchema = z.enum(['PENDIENTE', 'CONCILIADO', 'IGNORADO']);
/** IBAN sin espacios ni guiones, en mayúsculas. */
export const IbanSchema = z
    .string()
    .trim()
    .transform((s) => s.replace(/[\s-]/g, '').toUpperCase())
    .refine((s) => /^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(s), { message: 'IBAN no válido' });
export const BankAccountDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    iban: z.string(),
    accountCode: z.string(),
    currency: z.string(),
    active: z.boolean(),
    lastBalanceCents: z.number().int().nullable(),
    lastBalanceAt: z.string().nullable(),
    /**
     * Saldo AL DÍA deducido del feed: el que dijo el banco más los movimientos
     * llegados después. `null` cuando no se puede afirmar (ver
     * `lib/banking/current-balance.ts`), y entonces se enseña solo el del banco.
     * `.default(null)` para que un cliente viejo no reviente contra un backend nuevo.
     */
    computedBalanceCents: z.number().int().nullable().default(null),
    /** Movimientos posteriores a la lectura del saldo: cuántos y por cuánto. */
    balanceDeltaCents: z.number().int().default(0),
    balanceDeltaCount: z.number().int().min(0).default(0),
    /** Movimientos sin conciliar: es el número que importa del día a día. */
    pendingCount: z.number().int().min(0),
    /** Origen de los movimientos: MANUAL (solo N43) o ENABLE_BANKING (PSD2). */
    feed: z.string().default('MANUAL'),
    /** Fin del consentimiento PSD2; hay que renovarlo antes de esa fecha. */
    feedValidUntil: z.string().nullable().default(null),
    feedLastSyncAt: z.string().nullable().default(null),
    feedLastError: z.string().nullable().default(null),
});
export const BankAccountListResponseSchema = z.object({
    items: z.array(BankAccountDtoSchema),
});
export const CreateBankAccountSchema = z.object({
    name: z.string().trim().min(1).max(120),
    iban: IbanSchema,
    /** Subcuenta de tesorería (572…). */
    accountCode: z.string().trim().regex(/^\d{3,8}$/, 'Cuenta contable no válida'),
    currency: z.string().trim().length(3).default('EUR'),
});
export const UpdateBankAccountSchema = CreateBankAccountSchema.partial().extend({
    active: z.boolean().optional(),
});
export const BankTransactionDtoSchema = z.object({
    id: z.string(),
    bankAccountId: z.string(),
    operationDate: z.string(),
    valueDate: z.string(),
    amountCents: z.number().int(),
    balanceCents: z.number().int().nullable(),
    concept: z.string(),
    reference: z.string().nullable(),
    documentNumber: z.string().nullable(),
    status: BankTransactionStatusSchema,
    /**
     * Céntimos del movimiento ya aplicados (sin signo). Lo que falta por aplicar
     * es `Math.abs(amountCents) - appliedCents`; mientras sea mayor que cero el
     * apunte sigue pendiente aunque ya haya pagado alguna factura.
     * `.default(0)` para que un cliente viejo no reviente contra un backend nuevo.
     */
    appliedCents: z.number().int().default(0),
    /**
     * Todo lo que ha pagado este movimiento, en orden. Un apunte puede cubrir
     * varias facturas; `matchedInvoiceId` solo guarda la primera y se conserva por
     * compatibilidad. `kind: 'writeoff'` es el sobrante dado por cerrado.
     */
    applications: z
        .array(z.object({
        kind: z.string(),
        targetId: z.string().nullable(),
        label: z.string().nullable(),
        amountCents: z.number().int(),
        /** Apunte con el que se anuló, cuando `kind = 'offset'`. */
        offsetTransactionId: z.string().nullable().default(null),
        /** Cuenta del plan contra la que se contabilizó, cuando `kind = 'account'`. */
        accountCode: z.string().nullable().default(null),
    }))
        .default([]),
    matchedInvoiceId: z.string().nullable(),
    matchedExpenseId: z.string().nullable(),
    /** Referencia legible de lo conciliado ("F-2026-0007", "2026/45"). */
    matchedLabel: z.string().nullable(),
    reconciledAt: z.string().nullable(),
    notes: z.string().nullable(),
});
export const BankTransactionListResponseSchema = z.object({
    items: z.array(BankTransactionDtoSchema),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
    /** Totales del filtro actual, para no tener que sumarlos a ojo. */
    totals: z.object({
        inCents: z.number().int(),
        outCents: z.number().int(),
        pendingCount: z.number().int().min(0),
    }),
});
export const BankStatementDtoSchema = z.object({
    id: z.string(),
    fileName: z.string(),
    format: z.string(),
    fromDate: z.string(),
    toDate: z.string(),
    openingBalanceCents: z.number().int(),
    closingBalanceCents: z.number().int(),
    transactionCount: z.number().int().min(0),
    importedCount: z.number().int().min(0),
    duplicateCount: z.number().int().min(0),
    importedAt: z.string(),
    importedByName: z.string().nullable(),
});
/** Resultado de importar un fichero de extracto. */
export const ImportStatementResponseSchema = z.object({
    statement: BankStatementDtoSchema,
    /** Avisos del parser que no invalidan el fichero. */
    warnings: z.array(z.string()),
    /**
     * Contraste del saldo final del banco con el saldo CONTABLE de la subcuenta.
     * Si no coinciden, faltan asientos por conciliar (que es lo normal justo
     * después de importar) o hay un movimiento sin registrar.
     */
    balanceCheck: z
        .object({
        bankCents: z.number().int(),
        ledgerCents: z.number().int(),
        diffCents: z.number().int(),
    })
        // null cuando el fichero no trae saldo (un Excel sin columna de saldo): no hay
        // nada que contrastar.
        .nullable(),
});
/**
 * Vista previa de una importación (`?dryRun=1`): qué se ha entendido del fichero y
 * qué entraría, SIN escribir nada. Existe porque un Excel mal leído (una columna
 * equivocada, un signo al revés) contaminaría la tesorería y hoy no hay forma de
 * deshacerlo a mano.
 */
export const ImportPreviewResponseSchema = z.object({
    preview: z.literal(true),
    fileName: z.string(),
    format: z.string(),
    /** Solo en hojas de cálculo: qué columna del Excel se ha tomado como qué. */
    detected: z
        .object({
        sheet: z.string(),
        headerRow: z.number().int(),
        columns: z.array(z.object({ role: z.string(), labels: z.array(z.string()) })),
        reversed: z.boolean(),
        /** Cuántas filas encadenan su saldo con la anterior, de cuántas se pudo mirar. */
        balanceChain: z.object({ ok: z.number().int(), checked: z.number().int() }).nullable(),
    })
        .nullable(),
    /** La cuenta que dice el fichero y si es la elegida (null si el fichero no lo dice). */
    declaredAccount: z.string().nullable(),
    accountMatches: z.boolean().nullable(),
    total: z.number().int(),
    /** Lo que entraría de verdad. */
    willImport: z.number().int(),
    /** Ya estaban, con la misma huella. */
    duplicates: z.number().int(),
    /** Ya estaban por otra vía (misma fecha e importe): conexión, N43 u otro Excel. */
    overlapping: z.number().int(),
    fromDate: z.string(),
    toDate: z.string(),
    inCents: z.number().int(),
    outCents: z.number().int(),
    openingBalanceCents: z.number().int().nullable(),
    closingBalanceCents: z.number().int().nullable(),
    /** Saldo contable de la subcuenta a la fecha del fichero, y su diferencia con el banco. */
    ledgerCents: z.number().int().nullable(),
    diffCents: z.number().int().nullable(),
    samples: z.array(z.object({
        operationDate: z.string(),
        amountCents: z.number().int(),
        concept: z.string(),
        balanceCents: z.number().int().nullable(),
        status: z.enum(['new', 'duplicate', 'overlap']),
    })),
    warnings: z.array(z.string()),
});
export const MatchCandidateSchema = z.object({
    kind: z.enum(['invoice', 'expense']),
    id: z.string(),
    number: z.string(),
    partyName: z.string(),
    partyNif: z.string().nullable(),
    date: z.string(),
    /**
     * Lo que se puede aplicar contra este documento. Para un documento sin pagar
     * es lo que queda por cobrar/pagar; para un gasto YA PAGADO desde su ficha
     * (`alreadyPaid`) es lo pagado que aún no se ha cuadrado con ningún apunte.
     */
    pendingCents: z.number().int(),
    totalCents: z.number().int(),
    score: z.number(),
    reasons: z.array(z.string()),
    exactAmount: z.boolean(),
    /**
     * El gasto ya está pagado desde su ficha (por la empresa, no por un socio) y
     * no se ha cuadrado con el banco: conciliar NO paga nada, solo enlaza el
     * apunte con el pago que ya existe. Antes estos gastos ni se proponían, y
     * quedaban para siempre en «pagado, sin cuadrar».
     */
    alreadyPaid: z.boolean().default(false),
});
/**
 * Otro apunte del MISMO banco que se anula con este: una comisión y su
 * condonación, un cargo y su devolución. No hay factura ni gasto de por medio.
 */
export const OffsetCandidateSchema = z.object({
    id: z.string(),
    operationDate: z.string(),
    concept: z.string(),
    amountCents: z.number().int(),
    /** Lo que le queda por aplicar (sin signo). */
    remainingCents: z.number().int(),
    /** Cuadra al céntimo con lo que queda del apunte abierto. */
    exactAmount: z.boolean(),
});
export const MatchCandidatesResponseSchema = z.object({
    items: z.array(MatchCandidateSchema),
    /** `.default([])` para que un cliente viejo no reviente contra un backend nuevo. */
    offsets: z.array(OffsetCandidateSchema).default([]),
});
/** Búsqueda manual de candidatos: quien la escribe sabe lo que busca. */
export const MatchCandidatesQuerySchema = z.object({
    q: z.string().trim().min(2).max(80).optional(),
});
/** Confirmación de conciliación: siempre la decide una persona. */
export const ReconcileTransactionSchema = z.object({
    kind: z.enum(['invoice', 'expense']),
    targetId: z.string().min(1),
    /**
     * `pay` (por defecto): registra el cobro o el pago, con su asiento.
     * `link`: el gasto YA está pagado desde su ficha; solo se enlaza el apunte con
     * ese pago, sin mover un céntimo ni crear asiento. Es explícito y no se
     * deduce del estado del gasto: si entre que se propuso y se pulsó alguien lo
     * pagó, un `pay` sobre un gasto pagado sigue fallando con 409, como siempre.
     */
    mode: z.enum(['pay', 'link']).default('pay'),
    /**
     * Solo en modo `link`: cuadrar MENOS de lo que el gasto tiene pagado y sin
     * cuadrar. Sin esto el servidor lo rechaza, porque el caso típico —el OCR
     * leyó 250 $ como 250 € y el banco cargó 217,80 €— dejaría 32,20 € pagados
     * en el libro sin apunte en el banco, escondidos para siempre. Se admite
     * cuando la persona lo pide con conocimiento (un pago que el banco cargó en
     * dos apuntes).
     */
    partial: z.boolean().default(false),
    /**
     * Importe a aplicar, en céntimos positivos. Si se omite se aplica el importe
     * del movimiento, que es el caso normal; se admite menos para movimientos
     * que agrupan varias facturas.
     */
    amountCents: z.number().int().positive().optional(),
    notes: z.string().max(500).optional(),
});
/**
 * Compensar dos apuntes entre sí. Los dos quedan aplicados por el mismo
 * importe; si uno es mayor que el otro, el sobrante sigue vivo y visible.
 */
export const OffsetTransactionSchema = z.object({
    counterpartId: z.string().min(1),
    /** Céntimos positivos; por defecto, el menor de los dos pendientes. */
    amountCents: z.number().int().positive().optional(),
    notes: z.string().max(500).optional(),
    /**
     * Si alguno de los dos apuntes encaja al céntimo con una factura o un gasto,
     * el servidor se niega a compensarlos: lo normal es que ESO sea lo que hay
     * que conciliar, y compensar dejaría la factura cobrada en el banco pero
     * abierta en el mayor. Con `force` la persona dice que ya lo ha mirado.
     */
    force: z.boolean().default(false),
});
/**
 * Reembolso a quien adelantó dinero de su bolsillo: el movimiento de salida del
 * banco se aplica contra su cuenta corriente de socio (551) en vez de contra
 * una factura o un gasto. Una sola transferencia puede saldar varios gastos a
 * la vez, que es como se hace en la práctica.
 */
export const SettlePartnerSchema = z.object({
    userId: z.string().min(1),
    /** Céntimos positivos. Si se omite, se aplica todo lo que queda del apunte. */
    amountCents: z.number().int().positive().optional(),
    /**
     * QUÉ gastos cubre esta transferencia, línea de reparto a línea de reparto.
     * Se elige a mano (decisión de Jesús, 21/08/2026) en vez de repartir por
     * antigüedad: así el reembolso dice exactamente lo que se ha devuelto.
     *
     * La suma tiene que cuadrar con el importe liquidado. Si se omite, el asiento
     * 551→572 se hace igual pero no se marca ningún gasto como reembolsado — útil
     * para saldar a mano una cuenta descuadrada, no para el uso normal.
     */
    allocations: z
        .array(z.object({
        expensePayerId: z.string().min(1),
        amountCents: z.number().int().positive(),
    }))
        .max(200)
        .optional(),
});
/**
 * Contabilizar el apunte contra una cuenta del plan, sin documento de por
 * medio: una aportación de socios (118), un traspaso entre cuentas propias
 * (57x), un impuesto cargado en cuenta (475). El asiento va contra la 572 de la
 * cuenta bancaria y el apunte queda conciliado con él.
 */
export const BookToAccountSchema = z.object({
    accountCode: z
        .string()
        .trim()
        .regex(/^\d{3,12}$/, 'El código de cuenta son entre 3 y 12 dígitos'),
    /** Concepto del asiento. Si se omite, el del propio apunte. */
    description: z.string().trim().max(200).optional(),
    /** Céntimos positivos. Si se omite, se aplica todo lo que queda del apunte. */
    amountCents: z.number().int().positive().optional(),
});
export const PartnerBalanceSchema = z.object({
    userId: z.string(),
    name: z.string(),
    accountCode: z.string(),
    /** Saldo acreedor: positivo = la empresa le debe ese dinero. */
    owedCents: z.number().int(),
});
export const PartnerBalanceListResponseSchema = z.object({
    items: z.array(PartnerBalanceSchema),
});
/** Un adelanto pendiente de devolver, para elegir qué cubre una transferencia. */
export const PartnerAdvanceSchema = z.object({
    expensePayerId: z.string(),
    expenseId: z.string(),
    code: z.string(),
    supplierName: z.string(),
    concept: z.string(),
    paidAt: z.string(),
    amountCents: z.number().int(),
    reimbursedCents: z.number().int(),
    pendingCents: z.number().int(),
});
export const PartnerAdvancesResponseSchema = z.object({
    items: z.array(PartnerAdvanceSchema),
    totalPendingCents: z.number().int(),
});
//# sourceMappingURL=banking.js.map